import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { AtSign, KeyRound, CheckCircle2 } from "lucide-react";

export default function AccountRecovery() {
  const [uContact, setUContact] = useState("");
  const [pContact, setPContact] = useState("");
  const [busyU, setBusyU] = useState(false);
  const [busyP, setBusyP] = useState(false);
  const [msgU, setMsgU] = useState("");
  const [msgP, setMsgP] = useState("");

  const recoverUsername = async (e) => {
    e.preventDefault();
    setBusyU(true);
    setMsgU("");
    try {
      const { data } = await api.post("/auth/forgot-username", { contact: uContact });
      setMsgU(data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusyU(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setBusyP(true);
    setMsgP("");
    try {
      const { data } = await api.post("/auth/forgot-password", { contact: pContact });
      setMsgP(data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusyP(false);
    }
  };

  return (
    <div className="container-x py-16 max-w-md" data-testid="account-recovery-page">
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#CC5A3A] mb-3">Account help</div>
      <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold">Recover your account</h1>
      <p className="text-[#5C6B64] mt-2">We'll send your username or a password reset link to the email or phone on file.</p>

      <div className="card-soft mt-8" data-testid="forgot-username-card">
        <h2 className="font-heading text-xl text-[#1E3F33] mb-2 flex items-center gap-2"><AtSign className="w-4 h-4 text-[#2D6A4F]" /> Forgot your username?</h2>
        <p className="text-sm text-[#5C6B64] mb-3">Enter the email or phone on your account.</p>
        <form onSubmit={recoverUsername} className="space-y-3">
          <input data-testid="forgot-username-input" className="input-soft" placeholder="Email or phone" value={uContact} onChange={(e) => setUContact(e.target.value)} required />
          <button data-testid="forgot-username-submit" disabled={busyU} className="btn-primary w-full">{busyU ? "Sending…" : "Send my username"}</button>
        </form>
        {msgU && <div data-testid="forgot-username-msg" className="text-sm text-[#2D6A4F] mt-3 inline-flex items-start gap-1.5"><CheckCircle2 className="w-4 h-4 mt-0.5" /> {msgU}</div>}
      </div>

      <div className="card-soft mt-6" data-testid="forgot-password-card">
        <h2 className="font-heading text-xl text-[#1E3F33] mb-2 flex items-center gap-2"><KeyRound className="w-4 h-4 text-[#2D6A4F]" /> Forgot your password?</h2>
        <p className="text-sm text-[#5C6B64] mb-3">Enter your username, email, or phone.</p>
        <form onSubmit={resetPassword} className="space-y-3">
          <input data-testid="forgot-password-input" className="input-soft" placeholder="Username, email or phone" value={pContact} onChange={(e) => setPContact(e.target.value)} required />
          <button data-testid="forgot-password-submit" disabled={busyP} className="btn-primary w-full">{busyP ? "Sending…" : "Send reset link"}</button>
        </form>
        {msgP && <div data-testid="forgot-password-msg" className="text-sm text-[#2D6A4F] mt-3 inline-flex items-start gap-1.5"><CheckCircle2 className="w-4 h-4 mt-0.5" /> {msgP}</div>}
      </div>

      <p className="text-sm text-[#5C6B64] mt-6">
        Remembered it? <Link to="/login" data-testid="recover-to-login" className="text-[#CC5A3A] font-medium">Back to sign in</Link>
      </p>
    </div>
  );
}
